import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, model, output } from '@angular/core';
import { Bucket, Transaction } from 'src/app/types';
import { byCount } from 'src/util/helpers';
import { UiComboboxOption } from '../../ui/combobox/combobox-option';
import { UiComboboxComponent } from '../../ui/combobox/combobox.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { DrawerService } from '../../services/drawer.service';

export type BucketSelectorState = 'saved' | 'suggested' | 'unsaved' | 'empty';
export type BucketSelectorSize = 'compact' | 'touch' | 'responsive';

@Component({
  selector: 'bucket-selector',
  templateUrl: './bucket-selector.component.html',
  styleUrl: './bucket-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-flex min-w-0 max-w-full',
    '[class.leading-none]': 'true',
  },
  imports: [UiComboboxComponent, UiButtonDirective, UiIconComponent],
})
export class BucketSelectorComponent {
  readonly transaction = input<Transaction>();
  readonly transactions = input<Transaction[]>();
  readonly selectedBucket = input<Bucket>();
  readonly needsSaving = input(false);
  readonly buckets = input<Bucket[]>([]);
  readonly fillWidth = input(false);
  readonly size = input<BucketSelectorSize>('compact');
  readonly width = input<string>();
  readonly menuOpen = model(false);
  /**
   * Receives a budget created from "+ Add new budget" in place of `changeSelectedBucket`. Pass one
   * when this selector can be destroyed while the drawer is open (virtual-list rows); it must not
   * depend on the selector's host component, because it outlives it.
   */
  readonly createdBucketTarget = input<(bucket: Bucket) => void>();
  readonly changeSelectedBucket = output<Bucket>();
  readonly saveBucket = output<Bucket>();
  readonly changeSuggestedBuckets = output<Bucket[]>();
  private readonly drawer = inject(DrawerService);
  private releaseAddBucketDrawer?: () => void;

  readonly suggestedBuckets = computed(() => this.getSuggestedBuckets());
  private suggestedBucketHeadId?: string;
  readonly bucketOptions = computed(() => this.buckets().map(bucket => this.toBucketOption(bucket)));
  readonly suggestedBucketOptions = computed(() => this.suggestedBuckets().map(bucket => this.toBucketOption(bucket)));
  readonly selectedBucketOption = computed(() => {
    const selectedBucket = this.selectedBucket();
    return selectedBucket ? this.toBucketOption(selectedBucket) : null;
  });
  readonly hasSelectedUnsavedBucket = computed(() => !!this.selectedBucket() && this.needsSaving());

  constructor() {
    inject(DestroyRef).onDestroy(() => this.releaseAddBucketDrawer?.());
    effect(() => {
      const newSuggestedBuckets = this.suggestedBuckets();
      const newHeadId = newSuggestedBuckets[0]?.id;
      if (this.suggestedBucketHeadId === newHeadId) return;
      this.suggestedBucketHeadId = newHeadId;
      this.changeSuggestedBuckets.emit(newSuggestedBuckets);
    });
  }

  get state(): BucketSelectorState {
    const selectedBucket = this.selectedBucket();
    if (!selectedBucket) return 'empty';
    if (!this.needsSaving()) return 'saved';
    const savedBucketId = this.transaction()?.bucketId;
    const topSuggestionId = this.suggestedBuckets()[0]?.id;
    if (!savedBucketId && topSuggestionId && topSuggestionId === selectedBucket.id) return 'suggested';
    return 'unsaved';
  }

  get showSaveButton(): boolean {
    return this.hasSelectedUnsavedBucket();
  }

  onSave(event: Event) {
    event.stopPropagation();
    const selectedBucket = this.selectedBucket();
    if (!selectedBucket) return;
    this.saveBucket.emit(selectedBucket);
  }

  getBucketsFromIds(ids: string[] = []) {
    const buckets = this.buckets();
    return ids
      .map(id => buckets.find(bucket => bucket.id === id))
      .filter((bucket): bucket is Bucket => !!bucket)
      .splice(0, 3);
  }

  getSuggestedBuckets() {
    const transaction = this.transaction();
    const transactions = this.transactions();
    if (!transactions && !transaction?.suggestedBucketIds?.length) return [];
    if (transaction?.suggestedBucketIds) return this.getBucketsFromIds(transaction.suggestedBucketIds);

    if (transactions?.length) {
      const firstTransaction = transactions[0];
      if (transactions.length === 1 && firstTransaction?.suggestedBucketIds?.length)
        return this.getBucketsFromIds(firstTransaction.suggestedBucketIds).slice(0, 3);
      const suggestions = transactions.reduce<string[]>((acc, transaction) => {
        if (!transaction.suggestedBucketIds) return acc;
        return [...acc, ...transaction.suggestedBucketIds];
      }, []);
      return this.getBucketsFromIds(byCount(suggestions)).slice(0, 3);
    }

    return [];
  }

  onBucketOptionChange(option: UiComboboxOption<Bucket> | null) {
    if (!option) return;

    this.changeSelectedBucket.emit(option.value);
    this.menuOpen.set(false);
  }

  openAddBudgetDialog() {
    this.menuOpen.set(false);
    const createdBucketTarget = this.createdBucketTarget();
    if (createdBucketTarget) {
      // Not released on destroy: the drawer service drops it when the drawer closes.
      this.drawer.openAddBucketDrawer(undefined, createdBucketTarget);
      return;
    }

    this.releaseAddBucketDrawer = this.drawer.openAddBucketDrawer(undefined, (bucket: Bucket) =>
      this.changeSelectedBucket.emit(bucket),
    );
  }

  private toBucketOption(bucket: Bucket): UiComboboxOption<Bucket> {
    return {
      key: bucket.id,
      label: bucket.name,
      value: bucket,
    };
  }
}
